/**
 * Editor state shape — the single source of truth for every UI module (plan D1, D25).
 *
 * Everything here is plain data: no DOM nodes, no `three` objects, no functions. The
 * non-serializable side (imported model, bound rig, GLB buffer) lives in `session.ts`.
 * State is treated as immutable: actions replace the objects on the changed path only, so UI
 * code can diff slices by reference.
 */

import type {
  CanonicalAnimationClip,
  CanonicalBoneId,
  CanonicalRigPlacement,
  ClipboardPayload,
  IkChainName,
  KeyframeSelection,
  MappingPreset,
  Pose,
  QuaternionTuple,
  SkinParams,
  Vec3Tuple,
} from '../shared';
import type { TestPoseId } from '../rig/autoskin/types';
import { defaultSkinParams } from '../rig/autoskin/skinParams';

export type EditorMode = 'model' | 'rig' | 'map' | 'animate' | 'library' | 'export';

/** Modes in the order the toolbar shows them. */
export const EDITOR_MODES: readonly EditorMode[] = [
  'model',
  'rig',
  'map',
  'animate',
  'library',
  'export',
];

export interface ProjectMeta {
  id: string;
  name: string;
  kind: 'rigged' | 'unrigged';
  /** True once the model (and, for a reopened project, its rig) is in the session. */
  loaded: boolean;
}

/**
 * A read-only preview character loaded beside the open project ("companion").
 *
 * Plain data only: the live root and rig are session objects (`SessionCompanion`), and this is
 * what the panels render from. Nothing here is persisted — reopening the editor starts with no
 * companions.
 */
export interface CompanionMeta {
  projectId: string;
  name: string;
  kind: 'rigged' | 'unrigged';
}

/** How many companions may be loaded at once. Each one is a whole extra character in the scene. */
export const MAX_COMPANIONS = 3;

export interface SelectionState {
  bone: CanonicalBoneId | null;
}

/**
 * How the editor points at one animation event. Events carry no id — `(frame, name)` is their
 * identity, and `normalizeClip` collapses exact duplicates so the pair is unambiguous.
 */
export interface ClipEventRef {
  frame: number;
  name: string;
}

export interface PlayheadState {
  /** Integer while scrubbing, fractional during playback. */
  frame: number;
  playing: boolean;
  loop: boolean;
  /**
   * Playback rate multiplier applied by `tick` only — 1 is real time. Never serialized into a
   * clip and never affects the evaluator, so a slow-motion review cannot change what is saved.
   * Always a number in [PLAYBACK_SPEED_MIN, PLAYBACK_SPEED_MAX]; optional would let an
   * `undefined` reach `tick` and turn the playhead into NaN.
   */
  speed: number;
}

export const PLAYBACK_SPEED_MIN = 0.05;
export const PLAYBACK_SPEED_MAX = 4;

export type VisibleBonesFilter = 'all' | 'keyed' | 'selected';

export interface TimelineState {
  snap: boolean;
  /** Horizontal pixels-per-frame multiplier; clamped to [TIMELINE_ZOOM_MIN, TIMELINE_ZOOM_MAX]. */
  zoom: number;
  scrollFrame: number;
  visibleBones: VisibleBonesFilter;
}

export const TIMELINE_ZOOM_MIN = 0.1;
export const TIMELINE_ZOOM_MAX = 32;

/** Editor-only IK handle. Never serialized into a clip (SPEC "Critical serialization rule"). */
export interface IkTargetState {
  position: Vec3Tuple;
  pole?: Vec3Tuple;
  active: boolean;
}

export interface IkState {
  enabled: boolean;
  targets: Partial<Record<IkChainName, IkTargetState>>;
}

export type MappingConfidence = 'confident' | 'uncertain' | 'missing';

export interface MappingValidationError {
  bone?: CanonicalBoneId;
  code: string;
  message: string;
}

export interface MappingValidationState {
  ok: boolean;
  errors: MappingValidationError[];
  /**
   * Non-blocking findings (`validateMapping().warnings`): the mapping is usable, but something
   * about it deserves a look. Always an array — the action fills it in when a caller omits it.
   */
  warnings: MappingValidationError[];
}

/** What `setMappingValidation` accepts: `warnings` may be omitted and defaults to `[]`. */
export type MappingValidationInput = Omit<MappingValidationState, 'warnings'> & {
  warnings?: MappingValidationError[];
};

export interface MappingState {
  draft: Partial<Record<CanonicalBoneId, string>>;
  confidence: Partial<Record<CanonicalBoneId, MappingConfidence>>;
  /** Bone names found in the imported skeleton, for the manual-correction dropdowns. */
  sourceBoneNames: string[];
  /** `null` until validation has run against the current draft. */
  validation: MappingValidationState | null;
  worldCorrection: QuaternionTuple;
  presets: MappingPreset[];
}

/** What the last leak check found, summed over the meshes. `null` = no check since the last solve. */
export interface LeakSummary {
  poseId: TestPoseId;
  count: number;
  /** Metres — the largest displacement among the leaked vertices. */
  maxDisplacement: number;
}

/** The one-click correction tool armed in RIG mode. `'reassign'` = next mesh click moves a region. */
export type SkinTool = 'none' | 'reassign';

export interface RigState {
  placement: CanonicalRigPlacement | null;
  symmetry: boolean;
  selectedJoint: CanonicalBoneId | null;
  skinGenerated: boolean;
  /** Tunables of the voxel-geodesic solver; every change re-solves from the cached distances. */
  skinParams: SkinParams;
  /** Test-pose gallery selection (RIG mode only; cleared on mode change). */
  previewPose: TestPoseId | null;
  leakSummary: LeakSummary | null;
  skinTool: SkinTool;
  /** True while voxelization / geodesics run — the UI shows a spinner and blocks re-entry. */
  skinBusy: boolean;
}

/** Row shown in the library list — a cache of the persisted clip record, not the clip itself. */
export interface LibraryClipSummary {
  id: string;
  name: string;
  fps: number;
  durationFrames: number;
  updatedAt: number;
  /**
   * Resolved folder name — `DEFAULT_FOLDER` for a clip with no assignment. Resolved by the
   * controller, not stored on the clip record: folders are metadata in the `settings` store.
   */
  folder: string;
}

export interface LibraryState {
  clips: LibraryClipSummary[];
  /** Clips assigned to THIS project (SPEC "Model Animation Assignment"). */
  selectedClipIds: string[];
  /** User folder names in creation order; `DEFAULT_FOLDER` is implicit and never listed. */
  folders: string[];
}

export interface ToastState {
  kind: 'info' | 'error';
  message: string;
  /** Monotonic per store, so a UI can tell a repeat message from a stale one. */
  id: number;
}

/**
 * What the last GLB export produced, kept so EXPORT mode can still show it after the panel was
 * unmounted (a mode switch rebuilds the columns from scratch, so panel-local state is lost).
 * Plain data on purpose: the `GlbInspection` this is distilled from carries track arrays that
 * have no business living in the store.
 */
export interface LastExportSummary {
  filename: string;
  boneNames: string[];
  clips: { name: string; duration: number }[];
  hasPositionOrScaleTracks: boolean;
  /**
   * How many of the exported clips carry the ONE position track the format allows — the Hips
   * vertical offset (`hipsY`). It is counted separately from `hasPositionOrScaleTracks`, which
   * stays the invariant alarm ("a position or scale track we never meant to write"): a crouch is
   * legal data, so the panel has to be able to say "Hips only (vertical offset)" instead of
   * either lying about there being none or shouting that the invariant broke.
   */
  hipsPositionTrackCount: number;
  /**
   * The texture side of a texture-free export, or `null` when the GLB carries its own textures.
   *
   * Always written, never merged into a previous summary: a stripped export followed by a normal
   * one must not leave the panel claiming image files the last export never produced.
   */
  textures: LastExportTextures | null;
  /** `Date.now()` at the moment the download was handed to the browser. */
  at: number;
}

/** What the texture-free export left OUT of the GLB and downloaded beside it. */
export interface LastExportTextures {
  /** Image files handed to the browser. `0` for a model that had no textures at all. */
  fileCount: number;
  /** Their combined size — the bytes the GLB no longer carries. */
  totalBytes: number;
  manifestFile: string;
  /** Materials the manifest describes. */
  materialCount: number;
  /** Textures the extractor could not carry, verbatim, so the panel can show them. */
  warnings: string[];
}

export interface UiState {
  toast: ToastState | null;
  /** Non-null while a long operation runs; the string is the label to show. */
  busy: string | null;
  errors: string[];
  /**
   * True while a persistence write is queued or in flight. `beforeunload` cannot await
   * IndexedDB, so "is my work on disk yet?" has to be answerable by looking at the toolbar
   * rather than by trusting the unload handler.
   */
  saving: boolean;
  /**
   * Set when IndexedDB refuses to upgrade or delete because another tab holds the old
   * connection. The user cannot guess that from a spinner — the UI says which tab to close.
   */
  dbBlocked: boolean;
  /** The last finished GLB export, or `null` when none ran in this session. */
  lastExport: LastExportSummary | null;
}

export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel?: string;
  redoLabel?: string;
}

export interface EditorState {
  mode: EditorMode;
  project: ProjectMeta | null;
  /**
   * Mirrors `session.rig !== null`. The mode gate is a pure function of state, and EXPORT needs a
   * bound rig — without this flag the toolbar would have to reach into the session, which no
   * action may do. Maintained by the controllers at every bind/clear site.
   */
  rigBound: boolean;
  /** Read-only preview characters loaded beside the open project, in load order. */
  companions: CompanionMeta[];
  selection: SelectionState;
  hover: CanonicalBoneId | null;
  /** The clip being edited — a copy; the library holds the persisted one. */
  clip: CanonicalAnimationClip | null;
  clipDirty: boolean;
  playhead: PlayheadState;
  /**
   * The un-keyed working pose. FK/IK edits land here; keyframing copies from here into the clip.
   * Recomputed from the clip whenever the playhead moves (setPlayhead/tick/stop) or
   * `resetLivePose()` runs — clip edits never touch it, so un-keyed work survives them.
   */
  livePose: Pose;
  /**
   * The un-keyed vertical Hips offset that travels BESIDE `livePose` (normalized: 0 = bind
   * height, +1 = one leg length up). It is a scalar, not a bone, so it cannot live inside `Pose`
   * — ten places enumerate that map with `Object.keys(pose) as CanonicalBoneId[]` and a non-bone
   * key there would be read as a bone.
   *
   * It FOLLOWS `livePose` everywhere: every writer that re-derives the pose from the clip
   * re-derives this from `evaluateHipsY`, and every undo entry that snapshots the pose snapshots
   * this in the SAME entry. Anything else and Ctrl+Z leaves the pelvis at a height the clip does
   * not produce, which the next `K` then writes into the clip.
   */
  liveHipsY: number;
  keySelection: KeyframeSelection;
  /**
   * The event marker the timeline has selected, or `null`. A `(frame, name)` REF, not an index:
   * the event array re-sorts after every edit, so an index would quietly start addressing a
   * different marker the moment a drag crossed another one.
   */
  selectedEvent: ClipEventRef | null;
  clipboard: ClipboardPayload | null;
  timeline: TimelineState;
  ik: IkState;
  mapping: MappingState;
  rig: RigState;
  library: LibraryState;
  ui: UiState;
  history: HistoryState;
}

/** Maximum number of entries kept in `ui.errors` (oldest dropped first). */
export const MAX_UI_ERRORS = 50;

export function createInitialState(overrides?: Partial<EditorState>): EditorState {
  const base: EditorState = {
    mode: 'model',
    project: null,
    rigBound: false,
    companions: [],
    selection: { bone: null },
    hover: null,
    clip: null,
    clipDirty: false,
    playhead: { frame: 0, playing: false, loop: true, speed: 1 },
    livePose: {},
    liveHipsY: 0,
    keySelection: [],
    selectedEvent: null,
    clipboard: null,
    timeline: { snap: true, zoom: 1, scrollFrame: 0, visibleBones: 'all' },
    ik: { enabled: false, targets: {} },
    mapping: {
      draft: {},
      confidence: {},
      sourceBoneNames: [],
      validation: null,
      worldCorrection: [0, 0, 0, 1],
      presets: [],
    },
    rig: {
      placement: null,
      symmetry: true,
      selectedJoint: null,
      skinGenerated: false,
      skinParams: defaultSkinParams(),
      previewPose: null,
      leakSummary: null,
      skinTool: 'none',
      skinBusy: false,
    },
    library: { clips: [], selectedClipIds: [], folders: [] },
    ui: {
      toast: null,
      busy: null,
      errors: [],
      saving: false,
      dbBlocked: false,
      lastExport: null,
    },
    history: { canUndo: false, canRedo: false },
  };
  return overrides ? { ...base, ...overrides } : base;
}
